/**
 * V2 Places — Google place search/nearby/details/geocode through the
 * server-side proxy (v2/adventure/api/google-proxy.php; key stays on
 * the server), plus location creation via the same upsert_location RPC
 * V1 uses. Mappings, Plus-Code rejection, and area-type rejection are
 * ported from V1's create-yoink.js — same rules, same shapes.
 */
import * as gateway from '../../core/gateway.js';
import { getApiBase } from '../../core/config.js';
import { getLocationGeo, extractGeoFromPlaceDetails } from '../../../assets/js/location-service.js';

const PROXY = `${getApiBase()}/google-proxy.php`;
const NEARBY_SEARCH_RADIUS = 150; // meters
const PLUS_CODE_PATTERN = /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}$/i;
const REJECTED_LOCATION_TYPES = [
  // V1's FULL list (create-yoink.js:53-71) — the M8.5 audit caught
  // continent/sublocality/neighborhood missing here, which let "SoHo"
  // count as a place again. A rule that took real iteration; keep whole.
  'continent', 'country',
  'administrative_area_level_1', 'administrative_area_level_2',
  'administrative_area_level_3', 'administrative_area_level_4',
  'administrative_area_level_5',
  'locality', 'postal_town', 'colloquial_area',
  'sublocality', 'sublocality_level_1', 'sublocality_level_2',
  'sublocality_level_3', 'sublocality_level_4', 'sublocality_level_5',
  'neighborhood',
];
const ADDRESS_TYPES = ['street_address', 'route', 'premise', 'subpremise', 'street_number', 'postal_code'];

export function isPlusCode(name) {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  if (PLUS_CODE_PATTERN.test(trimmed)) return true;
  return PLUS_CODE_PATTERN.test(trimmed.split(',')[0].trim());
}

export function isRejectedLocationType(types) {
  return Array.isArray(types) && types.some((t) => REJECTED_LOCATION_TYPES.includes(t));
}

/** Autocomplete suggestions: [{ placeId, mainText, secondaryText }] (areas/addresses filtered out like V1). */
export async function searchGooglePlaces(query) {
  const response = await fetch(`${PROXY}?action=places-autocomplete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input: query, languageCode: 'en' }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return (data.suggestions || [])
    .map((s) => s.placePrediction)
    .filter((p) => {
      if (!p) return false;
      if ((p.types || []).some((t) => ADDRESS_TYPES.includes(t))) return false;
      const mainText = p.structuredFormat?.mainText?.text || p.text?.text || '';
      return mainText && !isPlusCode(mainText);
    })
    .map((p) => ({
      placeId: p.placeId,
      mainText: p.structuredFormat?.mainText?.text || p.text?.text || '',
      secondaryText: p.structuredFormat?.secondaryText?.text || '',
    }));
}

/** GPS nearby places mapped to the V1 location shape; Plus-Code names filtered. */
export async function nearbyPlaces(lat, lng) {
  const response = await fetch(`${PROXY}?action=places-nearby`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lng, radius: NEARBY_SEARCH_RADIUS, maxResults: 8 }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return (data.places || [])
    .map((place) => {
      let displayName = place.displayName?.text;
      if (!displayName || isPlusCode(displayName)) {
        const parts = (place.formattedAddress || '').split(',').map((p) => p.trim());
        displayName = parts.find((p) => p && !isPlusCode(p)) || null;
      }
      return {
        display_name: displayName || 'Unknown',
        formatted_address: place.formattedAddress || '',
        lat: place.location?.latitude ?? lat,
        lng: place.location?.longitude ?? lng,
        google_place_id: place.id?.replace('places/', ''),
      };
    })
    .filter((p) => p.display_name !== 'Unknown' && !isPlusCode(p.display_name));
}

/**
 * Google Text Search — the one place-finder that returns
 * PERMANENTLY-CLOSED businesses (Autocomplete and Nearby Search both hide
 * them). Biased toward an anchor pin when given. Returns picker-shaped
 * rows carrying the place's own coordinates and businessStatus.
 */
export async function textSearchPlaces(query, lat, lng, maxResults = 20) {
  // Ask for the full 20 (proxy default is 8): Google ranks CLOSED places
  // near the bottom, so a closed spot behind eight operating look-alikes
  // simply never arrived. The proxy caps the query at 200 chars — slice
  // rather than 400.
  const payload = { query: String(query).slice(0, 200), maxResults };
  if (lat != null && lng != null) { payload.lat = lat; payload.lng = lng; }
  const response = await fetch(`${PROXY}?action=places-text-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return (data.places || [])
    .map((p) => ({
      placeId: p.id?.replace('places/', ''),
      mainText: p.displayName?.text || '',
      secondaryText: p.formattedAddress || '',
      formatted_address: p.formattedAddress || '',
      business_status: p.businessStatus || null,
      lat: p.location?.latitude ?? null,
      lng: p.location?.longitude ?? null,
    }))
    .filter((p) => p.placeId && p.mainText && !isPlusCode(p.mainText));
}

const MAPS_LINK_HOSTS = ['maps.app.goo.gl', 'goo.gl', 'maps.google.com', 'www.google.com', 'google.com', 'maps.apple.com'];

/**
 * Pull { name, placeId, lat, lng } out of a Google or Apple Maps URL —
 * originally the admin's escape hatch, now also the list pages'
 * paste-a-link flow. Handles /maps/place/NAME/@lat,lng…, /maps/search/NAME,
 * ?q= / ?query= forms, the Maps URLs API's query_place_id (a real Google
 * place ID — the one form that needs no search at all), and Apple's
 * ?q=NAME&ll=lat,lng. Returns null for anything else.
 */
export function parseMapsUrl(url) {
  let u;
  try { u = new URL(url); } catch { return null; }
  const host = u.hostname.toLowerCase();
  if (!MAPS_LINK_HOSTS.includes(host)) return null;
  const apple = host === 'maps.apple.com';
  const placeId = apple ? null : (u.searchParams.get('query_place_id') || '').trim() || null;
  let name;
  if (apple) {
    name = (u.searchParams.get('q') || u.searchParams.get('name') || u.searchParams.get('address') || '').trim();
  } else {
    const seg = u.pathname.match(/\/maps\/(?:place|search)\/([^/@]+)/);
    name = seg ? decodeURIComponent(seg[1].replace(/\+/g, ' ')).trim() : '';
    if (!name) name = (u.searchParams.get('q') || u.searchParams.get('query') || '').trim();
  }
  // Bare coordinates are no name to search — but with a query_place_id
  // riding along (the Maps URLs API's recommended pairing) the link still
  // names its exact place.
  if (/^-?\d+\.\d+,/.test(name)) name = '';
  if (!name && !placeId) return null;
  let lat = null;
  let lng = null;
  const at = url.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) {
    lat = parseFloat(at[1]);
    lng = parseFloat(at[2]);
  } else {
    const ll = (u.searchParams.get('ll') || u.searchParams.get('sll') || u.searchParams.get('coordinate') || '')
      .match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
    if (ll) {
      lat = parseFloat(ll[1]);
      lng = parseFloat(ll[2]);
    }
  }
  return { name, placeId, lat, lng };
}

/**
 * Resolve a pasted Google Maps link to { name, lat, lng }. Share links
 * (maps.app.goo.gl) carry nothing in the URL itself — the proxy expands
 * the redirect chain server-side and the full URL gets parsed instead.
 */
export async function resolveMapsLink(term) {
  let host;
  try { host = new URL(term).hostname.toLowerCase(); } catch { return null; }
  if (!MAPS_LINK_HOSTS.includes(host)) return null;
  const direct = parseMapsUrl(term);
  if (direct) return direct;
  const response = await fetch(`${PROXY}?action=expand-maps-link&url=${encodeURIComponent(term)}`);
  const data = await response.json();
  if (data.error || !data.url) return null;
  return parseMapsUrl(data.url);
}

/**
 * A pasted Maps link as add-to-list candidates, best match first —
 * the list pages' paste-a-link flow (2026-08-03). Returns:
 *   null                          — not a readable place link
 *   { rejected: true }            — the link points at an area, not a place
 *   { candidates: [picker rows] } — best first; alternates behind it
 * A query_place_id resolves directly (and can reject areas right here);
 * a name goes through Text Search anchored at the link's own pin, exact
 * name matches first, then nearest to the pin.
 */
export async function mapsLinkCandidates(term) {
  const parsed = await resolveMapsLink(term);
  if (!parsed) return null;
  if (parsed.placeId) {
    try {
      // businessStatus rides along (same Pro SKU as the basic mask) so
      // callers can warn about a place that has closed for good.
      const details = await placeDetails(parsed.placeId,
        'displayName,formattedAddress,location,types,businessStatus');
      if (isRejectedLocationType(details.types)) return { rejected: true };
      const mainText = details.displayName?.text || parsed.name;
      if (mainText && !isPlusCode(mainText)) {
        return {
          candidates: [{
            placeId: parsed.placeId,
            mainText,
            secondaryText: details.formattedAddress || '',
            businessStatus: details.businessStatus || null,
          }],
        };
      }
    } catch { /* details down — fall through to the name search */ }
  }
  if (!parsed.name) return null;
  const results = await textSearchPlaces(parsed.name, parsed.lat, parsed.lng).catch(() => []);
  const anchored = parsed.lat != null && parsed.lng != null;
  const ranked = results
    .map((r) => ({
      ...r,
      businessStatus: r.business_status || null,
      meters: anchored && r.lat != null ? distanceMeters(parsed.lat, parsed.lng, r.lat, r.lng) : null,
    }))
    .sort((a, b) => {
      const nameFirst = matchesName(parsed.name, b.mainText) - matchesName(parsed.name, a.mainText);
      if (nameFirst) return nameFirst;
      return (a.meters ?? Infinity) - (b.meters ?? Infinity);
    });
  return { candidates: ranked.slice(0, 4) };
}

/**
 * Route polyline via the proxy's directions action (On the way page).
 * Returns { encoded, distanceMeters, duration } — duration is Google's
 * "1234s" string. Throws when no route exists (over an ocean, say).
 */
export async function fetchRoute(origin, destination) {
  const response = await fetch(`${PROXY}?action=directions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ origin, destination }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  const route = data.routes?.[0];
  if (!route?.polyline?.encodedPolyline) throw new Error('No drivable route found.');
  return {
    encoded: route.polyline.encodedPolyline,
    distanceMeters: route.distanceMeters ?? null,
    duration: route.duration || null,
  };
}

/** Great-circle distance in meters between two lat/lng points. */
export function distanceMeters(aLat, aLng, bLat, bLng) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Find a Google place by NAME near an anchor pin — the fallback the
 * place-id backfill needs when radius Nearby Search comes up empty.
 *
 * Nearby Search drops permanently-closed (and low-prominence) businesses,
 * so a closed spot like a shuttered tasting room can never be linked that
 * way — and without a google_place_id its page shows no rich details and
 * no "Permanently closed" stamp.
 *
 * These legacy rows are also pinned to a GEOCODE of their address string,
 * not the business itself, so the coordinate can sit hundreds of meters —
 * or more — off the real place. That is why a proximity-gated pass skips
 * them ("no reliable match close by"). So the NAME is the anchor we trust:
 * Text Search (which DOES surface closed places, unlike Autocomplete) by
 * the row's name, keep only confident name matches, and use the pin only
 * as a loose tiebreaker and a wrong-city guard (`maxMeters`, generous by
 * design) — never as a precise gate. Returns the nearbyPlaces() shape
 * (with the place's OWN corrected coordinates), or null.
 */
export async function findPlaceByName(displayName, lat, lng, maxMeters = 50000) {
  if (!displayName) return null;
  // Text Search matches establishments on their name; the address tail of
  // a "Name, Street, City, …" display_name mostly just narrows it, so query
  // the name first and fall back to the full string only if nothing hits.
  const core = displayName.split(',')[0].trim() || displayName;
  // Keep only confident name matches — a wrong google_place_id would feed
  // wrong details forever, so an unrelated nearby place must never win.
  let results = await textSearchPlaces(core, lat, lng).catch(() => []);
  let named = results.filter((r) => matchesName(core, r.mainText));
  if (!named.length && core !== displayName) {
    // Retry with the full "Name, Street, City…" string: the address gives
    // Google the context that surfaces closed/delisted listings the bare
    // name misses. Zero results is NOT the only miss — a page of unrelated
    // look-alikes must trigger this too.
    results = await textSearchPlaces(displayName, lat, lng).catch(() => []);
    named = results.filter((r) => matchesName(core, r.mainText));
  }
  if (!named.length) {
    // Last try with NO bias: the bias circle itself can suppress a listing
    // outside the immediate area (a closed branch across town). The
    // maxMeters wrong-city guard below still protects the final pick.
    results = await textSearchPlaces(core, null, null).catch(() => []);
    named = results.filter((r) => matchesName(core, r.mainText));
  }
  const haveAnchor = lat != null && lng != null;
  let best = null;
  for (const r of named.slice(0, 8)) {
    if (r.lat == null || r.lng == null) continue;
    const meters = haveAnchor ? distanceMeters(lat, lng, r.lat, r.lng) : 0;
    if (haveAnchor && meters > maxMeters) continue; // wrong city, not a bad pin
    if (!best || meters < best.meters) {
      best = {
        meters,
        place: {
          display_name: r.mainText,
          formatted_address: r.formatted_address || '',
          lat: r.lat,
          lng: r.lng,
          google_place_id: r.placeId,
        },
      };
    }
  }
  return best?.place || null;
}

/** Normalized, punctuation-insensitive containment either way. */
export function matchesName(a, b) {
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const x = norm(a);
  const y = norm(b);
  return !!x && !!y && (x === y || x.includes(y) || y.includes(x));
}

/**
 * The place's own Google photo as a proxy URL (key stays server-side,
 * bytes disk-cached 7 days) — the stand-in image for an imported memory
 * whose owner has no photo of their own (2026-08-04 decision; a review
 * from 2019 rarely comes with one). Null when Google has no photo.
 *
 * ABSOLUTE on purpose (2026-08-05). It first shipped as a same-origin
 * path, which made it a THIRD shape in yoinks.images — and the nine V2
 * renderers, four PHP builders and all of V1 that test only
 * `startsWith('http')` handed it to the storage-URL builder and rendered
 * a 404. An absolute URL is indistinguishable from the legacy-URL shape
 * every one of them already handles, so nothing else has to change, and
 * OG unfurls (which require absolute URLs) work too.
 */
export async function fetchPlacePhoto(placeId) {
  if (!placeId) return null;
  try {
    const details = await placeDetails(placeId, 'photos');
    const name = details?.photos?.[0]?.name;
    if (!name) return null;
    const path = `${PROXY}?action=photo&photoName=${encodeURIComponent(name)}&maxHeight=800&maxWidth=1200`;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return path.startsWith('http') ? path : `${origin}${path}`;
  } catch {
    return null;
  }
}

/** Bare place details via the proxy (also used for profile location). */
export async function placeDetails(placeId, fields = 'displayName,formattedAddress,location,types') {
  const response = await fetch(
    `${PROXY}?action=place-details&placeId=${encodeURIComponent(placeId)}&fields=${encodeURIComponent(fields)}`
  );
  const details = await response.json();
  if (details.error) throw new Error(details.error);
  return details;
}

/**
 * Resolve an autocomplete pick to a PENDING place — everything
 * upsert_location needs, but no database row yet. The log flow used to
 * write the row at pick time, which littered the locations table with
 * orphans every time someone opened the form and walked away (the
 * admin's "Delete all orphans" backlog). The row is created at save
 * time instead, by savePendingLocation.
 * Returns { pending } or { rejected: true } for too-broad areas.
 */
export async function resolvePlaceFromSuggestion({ placeId, mainText }) {
  // addressComponents rides along so the row can be born with its
  // county/state/country — nothing lazily backfills V2 rows post-flip.
  const details = await placeDetails(placeId,
    'displayName,formattedAddress,location,types,addressComponents');
  if (isRejectedLocationType(details.types)) return { rejected: true };

  let displayName = details.displayName?.text;
  if (!displayName || isPlusCode(displayName)) {
    const parts = (details.formattedAddress || '').split(',').map((p) => p.trim());
    displayName = parts.find((p) => p && !isPlusCode(p)) || mainText;
  }
  if (isPlusCode(displayName)) displayName = mainText;

  return {
    pending: {
      pending: true,
      display_name: displayName,
      formatted_address: details.formattedAddress || '',
      lat: details.location?.latitude ?? null,
      lng: details.location?.longitude ?? null,
      google_place_id: placeId || null,
      geo: extractGeoFromPlaceDetails(details),
    },
  };
}

/**
 * A nearby-search hit (nearbyPlaces shape) as a pending place: no
 * network, no row — the GPS auto-select used to upsert a location the
 * moment the Log page opened, the single biggest orphan source.
 */
export function pendingFromNearby(place) {
  return {
    pending: true,
    display_name: place.display_name,
    formatted_address: place.formatted_address || '',
    lat: place.lat ?? null,
    lng: place.lng ?? null,
    google_place_id: place.google_place_id || null,
    geo: null, // Nearby Search carries no addressComponents — filled at save
  };
}

/**
 * Write a pending place to the database (save time). Geo that didn't
 * ride along at pick time is fetched best-effort — a miss just leaves
 * nulls for the nightly locations cleanup to backfill.
 */
export async function savePendingLocation(pending) {
  let geo = pending.geo;
  if (!geo?.state_province || !geo?.country) {
    try {
      geo = await getLocationGeo({
        placeId: pending.google_place_id, lat: pending.lat, lng: pending.lng,
      });
    } catch {
      geo = pending.geo || {};
    }
  }
  const { data: locationId, error } = await gateway.rawRpc('upsert_location', {
    params: {
      p_display_name: pending.display_name,
      p_formatted_address: pending.formatted_address || '',
      p_lat: pending.lat ?? null,
      p_lng: pending.lng ?? null,
      p_google_place_id: pending.google_place_id || null,
      p_county: geo?.county || null,
      p_state_province: geo?.state_province || null,
      p_country: geo?.country || null,
      p_country_code: geo?.country_code || null,
    },
  });
  if (error) throw error;
  return {
    id: locationId,
    display_name: pending.display_name,
    formatted_address: pending.formatted_address || '',
  };
}

/**
 * Resolve an autocomplete pick straight to a location row — for flows
 * where the place is referenced immediately (a list entry lands right
 * after), so deferred creation would buy nothing.
 * Returns { location } or { rejected: true } for too-broad areas.
 */
export async function createLocationFromPlace(suggestion) {
  const result = await resolvePlaceFromSuggestion(suggestion);
  if (result.rejected) return result;
  return { location: await savePendingLocation(result.pending) };
}
